import { useSelector } from 'react-redux';
import styled from 'styled-components';
import React, { ChangeEvent, Fragment, ReactElement, ReactNode, useState } from 'react';
import { FlowjoCommons, LicenseUtils, Product, ProductVersionUtils } from '@portal/portal-core';
import { NavigationActions } from 'actions';
import { TOOL_BUTTON_NAMES } from 'constants/user-account';
import { NO_ACTION } from 'constants/product-tab';
import {
  selectLicenseStatus,
  selectInfinicytProducts,
  selectProducts,
  selectCostCenters,
  selectCurrentUser,
} from 'selectors';
import { PRODUCT_INFO } from 'constants/products';
import { HollowButton } from 'components/product-buttons';
import { FjButton, FjTooltip, fjButtonTypes } from 'components/primitives';
import { chinaFlag, isProductVersionActive } from 'utils';
import { useAppDispatch } from 'store/hooks';
import {
  getInstallerUrl,
  getInstallerLabel,
  getInstructionsUrl,
  getUserAccessControlUrl,
  getUserAccessControlLabel,
  getReferenceDatabases,
  getReferenceDatabaseCategories,
  isAssetsV2,
} from 'utils/product-assets-utils';
import ProductSplash from '../product-splash';
import LicenseInfoLink from './license-info-link';

import heroImage from '../images/infinicyt-hero.png';

const { Regional } = FlowjoCommons;

/** Minimal shape of an Infinicyt product entry accessed by this tab. */
interface InfinicytProduct {
  id: string | number;
  name: string;
  productVersion?: string;
  assets?: Product.ProductAssets | null;
  whitelistedCountries?: string[];
  // Products originate from an untyped store slice; allow other fields.
  [key: string]: unknown;
}

/** Minimal shape of a cost center entry accessed by this tab. */
interface CostCenter {
  countryCode?: string | null;
}

interface InfinicytTabProps {
  productName: string;
  marketingContentOnly?: boolean;
}

type InfinicytLinkButtonProps = React.ComponentProps<typeof FjButton>;

const NEW_WINDOW_ATTRS = {
  rel: 'noopener noreferrer',
  target: '_blank',
};

const infinicytShorthand = 'BD Infinicyt™';
const requestADemoOrQuoteUrl = 'https://www.bdbiosciences.com/infinicyt';
const startTrialUrl = `/product-trial?productName=${Product.NAMES.infinicytRuo}`;
const regionRestrictedTooltipText = 'Not available in your region. Contact us for more information.';

const LinkHeader = styled.div`
  font-size: 1.5em;
  font-weight: bold;
  margin-top: 1em;
  margin-bottom: 1em;
`;

const VersionSelector = styled.div`
  margin-bottom: 2em;
  display: flex;
  align-items: center;
  gap: 1em;
`;

const VersionLabel = styled.label`
  font-weight: bold;
  font-size: 1em;
`;

const VersionSelect = styled.select`
  padding: 0.5em;
  font-size: 1em;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  min-width: 200px;

  &:hover {
    border-color: #888;
  }

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const NoVersionsInSelectorMessage = styled.div`
  padding: 1em;
  margin-bottom: 2em;
  font-size: 1em;
  color: #666;
  background-color: #f9f9f9;
  border-radius: 4px;
  text-align: center;
`;

const InfinicytLinkButton = (props: InfinicytLinkButtonProps): ReactElement => (
  <FjButton {...NEW_WINDOW_ATTRS} buttonType={fjButtonTypes.LINK} {...props} />
);

// Delegates to LicenseUtils.isBlockedCountry (embargo + whitelist, the same check enforced
// server-side) rather than hand-rolling a second, whitelist-only implementation -- this is the
// single gating source of truth shared by the version-availability messaging below and the
// trial CTA's region check.
const isCountryWhitelisted = (
  product: InfinicytProduct | null,
  licenseCountryCode: string | null | undefined,
  userCountryCode: string | null | undefined,
): boolean => {
  // If we have no country codes to check at all, block for safety. (This is slightly stricter
  // than the previous whitelist-only check for a product with an empty whitelist and no known
  // country code, which used to allow that case -- failing closed here is intentional and
  // consistent with the same fail-safe default used server-side.)
  if (!licenseCountryCode && !userCountryCode) {
    return false;
  }
  if (licenseCountryCode && LicenseUtils.isBlockedCountry(product, licenseCountryCode)) {
    return false;
  }
  if (userCountryCode && LicenseUtils.isBlockedCountry(product, userCountryCode)) {
    return false;
  }
  return true;
};

const InfinicytTab = ({
  productName,
  marketingContentOnly = false,
}: InfinicytTabProps): ReactElement => {
  const { productLicenseInfo, licenseState } = useSelector(selectLicenseStatus);
  // selectCostCenters comes from an untyped store slice.
  const costCenters = useSelector(selectCostCenters) as Record<string, CostCenter | undefined>;
  const currentUser = useSelector(selectCurrentUser);
  const infinicytProducts: InfinicytProduct[] = useSelector(selectInfinicytProducts);
  // selectInfinicytProducts is scoped to the user's existing license, so it's empty pre-trial;
  // the trial CTA's region check needs the latest active RUO version regardless of license state.
  const allProducts: InfinicytProduct[] = useSelector(selectProducts);

  const costCenter = productLicenseInfo?.costCenterId
    ? costCenters[productLicenseInfo.costCenterId]
    : null;
  const licenseCountryCode: string | null | undefined = costCenter?.countryCode;
  const userCountryCode: string | null | undefined = currentUser?.countryCode;

  // Only Infinicyt RUO offers a self-service trial; other Infinicyt tabs are unaffected.
  // Self-service trials are disabled in China (also enforced server-side in trial.ts).
  const isRuoTab = productName === Product.NAMES.infinicytRuo;
  const showTrialCta = isRuoTab && licenseState === NO_ACTION && !chinaFlag();

  const dispatch = useAppDispatch();
  const openLicenseDrawer = (): void => {
    dispatch(NavigationActions.openLeftDrawerWithTab(TOOL_BUTTON_NAMES.LICENSE));
  };

  const [selectedVersion, setSelectedVersion] = useState<string | null>(null); // null means use latest

  const handleVersionChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const version = event.target.value;
    setSelectedVersion(version === 'latest' ? null : version);
  };

  // Active/inactive filtering is handled server-side; filter by name only
  const matchingProducts = infinicytProducts.filter(
    (product) => product.name === productName || product.name === 'Infinicyt',
  );

  // Sort by productVersion descending (latest first)
  const sortedProducts = ProductVersionUtils.sortProductVersionsDescending(matchingProducts);

  const getCurrentProduct = (): InfinicytProduct | null => {
    if (!sortedProducts.length) {
      return null;
    }

    // If no version selected, return latest (first in sorted array)
    if (!selectedVersion) {
      return sortedProducts[0];
    }

    // Find the product with the selected version
    return (
      sortedProducts.find((product) => product.productVersion === selectedVersion) ||
      sortedProducts[0]
    );
  };

  const currentProduct = getCurrentProduct();

  // Reuses the same embargo + whitelist check enforced server-side (license-v2.ts, trial.ts) so
  // the trial CTA's gating always matches what the backend will actually allow. Looked up from
  // allProducts (not the license-gated currentProduct) since a trial-eligible user has no license
  // yet.
  const latestActiveRuoVersion = showTrialCta
    ? ProductVersionUtils.getLatestProductVersion(
        allProducts.filter(
          (product) =>
            product.name === Product.NAMES.infinicytRuo && isProductVersionActive(product),
        ),
      )
    : null;
  const trialRegionRestricted = showTrialCta
    ? !isCountryWhitelisted(latestActiveRuoVersion, licenseCountryCode, userCountryCode)
    : false;

  const productDisplayName = Product.PRODUCT_MARKETING_NAMES[productName] || infinicytShorthand;

  const hasAssets = (): boolean => {
    if (!currentProduct || !currentProduct.assets) {
      return false;
    }

    // Check if any asset categories exist
    const hasInstaller = !!getInstallerUrl(currentProduct.assets);
    const hasInstructions = !!getInstructionsUrl(currentProduct.assets);
    const hasUserAccessControl = !!getUserAccessControlUrl(currentProduct.assets);

    // Check reference databases (V1 or V2)
    const isV2 = isAssetsV2(currentProduct.assets);
    const hasReferenceDatabases = isV2
      ? getReferenceDatabaseCategories(currentProduct.assets)?.length > 0
      : getReferenceDatabases(currentProduct.assets)?.length > 0;

    return hasInstaller || hasInstructions || hasUserAccessControl || hasReferenceDatabases;
  };

  const getVersionUnavailableMessage = (): string | null => {
    const product = currentProduct;

    if (!product) {
      return null;
    }

    // Check if product is not whitelisted for this country
    if (!isCountryWhitelisted(product, licenseCountryCode, userCountryCode)) {
      const version = product.productVersion || 'unknown';
      const hasWhitelist = product.whitelistedCountries && product.whitelistedCountries.length > 0;

      if (hasWhitelist) {
        // Check user country first
        if (userCountryCode && !product.whitelistedCountries!.includes(userCountryCode)) {
          const userCountry = Regional.getCountryName(userCountryCode);
          return `${productDisplayName} Version ${version} is not available for ${userCountry} user accounts.`;
        }

        // Then check license country
        if (licenseCountryCode && !product.whitelistedCountries!.includes(licenseCountryCode)) {
          const licenseCountry = Regional.getCountryName(licenseCountryCode);
          return `${productDisplayName} Version ${version} is not available for ${licenseCountry} licenses.`;
        }
      }

      return `${productDisplayName} Version ${version} is not available.`;
    }

    // Check if product has no assets
    if (!hasAssets()) {
      return `No assets available for ${productDisplayName}.`;
    }

    return null;
  };

  const installerUrl = getInstallerUrl(currentProduct?.assets);
  const instructionsUrl = getInstructionsUrl(currentProduct?.assets);

  const renderVersionSelector = (): ReactElement | null => {
    // If no products are available, show a message — unless the user is trial-eligible: the
    // trial CTA already explains why there's nothing to show yet, so this would just be
    // confusing noise next to it.
    if (sortedProducts.length === 0) {
      if (showTrialCta) {
        return null;
      }
      return (
        <NoVersionsInSelectorMessage>
          No versions of this product are currently available.
        </NoVersionsInSelectorMessage>
      );
    }

    // Check if current version is unavailable (not enabled or not whitelisted)
    const unavailableMessage = getVersionUnavailableMessage();

    // Only show version selector if there are multiple versions
    if (sortedProducts.length <= 1) {
      // If there's only one version and it's unavailable, show the message
      if (unavailableMessage) {
        return <NoVersionsInSelectorMessage>{unavailableMessage}</NoVersionsInSelectorMessage>;
      }
      return null;
    }

    return (
      <Fragment>
        <VersionSelector>
          <VersionLabel htmlFor="version-select">Version:</VersionLabel>
          <VersionSelect
            id="version-select"
            value={selectedVersion || 'latest'}
            onChange={handleVersionChange}
          >
            <option value="latest">Latest ({sortedProducts[0]?.productVersion || 'N/A'})</option>
            {sortedProducts.slice(1).map((product) => (
              <option key={product.id} value={product.productVersion}>
                {product.productVersion}
              </option>
            ))}
          </VersionSelect>
        </VersionSelector>
        {unavailableMessage && (
          <NoVersionsInSelectorMessage>{unavailableMessage}</NoVersionsInSelectorMessage>
        )}
      </Fragment>
    );
  };

  const getProductButtons = (): Record<string, ReactNode> => {
    const requestADemoOrQuoteText = 'Request a demo or quote';

    const buttons: Record<string, ReactNode> = {};

    if (showTrialCta) {
      const trialCtaButton = (
        <HollowButton
          key="trial"
          href={trialRegionRestricted ? undefined : startTrialUrl}
          disabled={trialRegionRestricted}
          text="Free Trial"
        />
      );
      buttons.trialButton = trialRegionRestricted ? (
        <FjTooltip key="trial-tooltip" Component={<span>{regionRestrictedTooltipText}</span>}>
          {trialCtaButton}
        </FjTooltip>
      ) : (
        trialCtaButton
      );
    }

    buttons.demoOrQuoteButton = (
      <HollowButton
        {...NEW_WINDOW_ATTRS}
        key="demo"
        href={requestADemoOrQuoteUrl}
        text={requestADemoOrQuoteText}
      />
    );
    buttons.versionSelector = renderVersionSelector();

    // If current product is unavailable (not enabled or not whitelisted), don't show asset-dependent buttons
    const unavailableMessage = getVersionUnavailableMessage();
    if (unavailableMessage) {
      return buttons;
    }

    // Get URLs from product assets using utility functions (handles v1 and v2)
    const userAccessControlModuleUrl = getUserAccessControlUrl(currentProduct?.assets);
    const userAccessControlModuleLabel = getUserAccessControlLabel(currentProduct?.assets);

    // Installer section
    if (installerUrl) {
      buttons.softwareHeader = (
        <LinkHeader key="software-header">{infinicytShorthand} Software</LinkHeader>
      );
      const installerLabel =
        getInstallerLabel(currentProduct?.assets) || `${infinicytShorthand} Software`;
      buttons.downloadButtonWin = (
        <InfinicytLinkButton key="download-win-link" href={installerUrl}>
          {installerLabel}
        </InfinicytLinkButton>
      );
    }

    // Reference databases section
    // For V2 assets, show categories with their labels; for V1, show flat list
    const isV2 = isAssetsV2(currentProduct?.assets);

    if (isV2) {
      // V2: Show categories with labels
      const categories = getReferenceDatabaseCategories(currentProduct?.assets);

      if (categories && categories.length > 0) {
        categories.forEach((category) => {
          const categoryHeaderKey = `category-header-${category.id}`;
          const categoryLinksKey = `category-links-${category.id}`;

          buttons[categoryHeaderKey] = (
            <LinkHeader key={categoryHeaderKey}>{category.label}</LinkHeader>
          );

          if (category.items && category.items.length > 0) {
            const categoryLinks = category.items.map((item) => (
              <InfinicytLinkButton key={`${category.id}-${item.url}`} href={item.url}>
                {item.label || item.url}
              </InfinicytLinkButton>
            ));
            buttons[categoryLinksKey] = <Fragment key={categoryLinksKey}>{categoryLinks}</Fragment>;
          }
          // If category has no items, don't show it
        });
      }
      // If no categories, don't show anything
    } else {
      // V1 or legacy: Show flat list under single header
      const assetReferenceDatabases = getReferenceDatabases(currentProduct?.assets);
      if (assetReferenceDatabases && assetReferenceDatabases.length > 0) {
        buttons.referenceDatabasesHeader = (
          <LinkHeader key="reference-databases-header">Reference Databases</LinkHeader>
        );
        const referenceDatabaseLinks = assetReferenceDatabases.map((db) => (
          <InfinicytLinkButton key={`db-${db.name || db.url}`} href={db.url}>
            {db.name}
          </InfinicytLinkButton>
        ));
        buttons.referenceDatabases = (
          <Fragment key="reference-databases">{referenceDatabaseLinks}</Fragment>
        );
      }
      // If no databases, don't show anything
    }

    // User access control module section
    // Only show if URL exists in assets
    if (userAccessControlModuleUrl) {
      buttons.userAccessControlModuleHeader = (
        <LinkHeader key="uac-header">User Access Control Module</LinkHeader>
      );
      buttons.userAccessControlModule = (
        <InfinicytLinkButton href={userAccessControlModuleUrl}>
          {userAccessControlModuleLabel || 'LDAP OpenDJ v4.4.5'}
        </InfinicytLinkButton>
      );
    }

    // Installation instructions section
    // Only show if URL exists in assets
    if (instructionsUrl) {
      buttons.installationInstructionsHeader = (
        <LinkHeader key="installation-instructions-header">Installation Instructions</LinkHeader>
      );
      buttons.installationInstructions = (
        <InfinicytLinkButton href={instructionsUrl}>
          {infinicytShorthand} Installation Instructions
        </InfinicytLinkButton>
      );
    }

    return buttons;
  };

  const blurbs = (
    <Fragment>
      <div>
        <b>Automated Gating &amp; Identification</b>
        <p>
          Semi-automated analysis using reference databases and expert-guided automated reports of
          predefined assay panels.
        </p>
      </div>
      <div>
        <b>Clustering &amp; Automatic Population Separator</b>
        <p>
          Automatically group cells based on their immunophenotype with easy-to-use clustering
          algorithms.
        </p>
      </div>
      <div>
        <b>Create Databases</b>
        <p>Compare samples against databases you create using your own files.</p>
      </div>
      <div>
        <b>Maturation Databases</b>
        <p>
          Create maturation databases and have the software automatically adjust and divide events
          into population stages of new samples.
        </p>
      </div>
    </Fragment>
  );

  const header = 'Flow cytometry analysis software designed for the way you work';
  const description = (
    <Fragment>
      {infinicytShorthand} software is an advanced analysis software that offers a comprehensive
      suite of innovative tools for the analysis, integration, and interpretation of flow data.
    </Fragment>
  );

  const buttons = Object.values(getProductButtons());
  // Infinicyt entries in PRODUCT_INFO always carry a logo string; narrow the
  // shared `logoSvg: string | null` to the non-null shape ProductSplash expects.
  const productInfo = PRODUCT_INFO[productName] as { logoSvg: string; name: string };

  const licenseInfoLink = !marketingContentOnly ? (
    <LicenseInfoLink onClick={openLicenseDrawer} />
  ) : null;

  return (
    <ProductSplash
      header={header}
      description={description}
      additionalContent={blurbs}
      heroImg={{ src: heroImage, alt: 'Infinicyt product preview', width: '60%' }}
      buttons={marketingContentOnly ? null : buttons}
      productInfo={productInfo}
      licenseInfoLink={licenseInfoLink}
    />
  );
};

export default InfinicytTab;
